import { formatEther, formatUnits, parseEther, parseUnits } from 'ethers/lib/utils';
import { UserPositionRaw } from '../../../../hooks/web3/trade/useUserPositions';
import { useMemo } from 'react';
import ClosePositionPreview from '../ClosePositionPreview';
import { calculatePnl, formatNonNegative } from '../../../../helpers';
import useUserFee from '../../../../hooks/web3/useUserFee';
import useTokenStats from '../../../../hooks/web3/useTokenStats';
import { config } from '../../../../config/config';

type LimitPreviewProps = {
    userPosition: UserPositionRaw | undefined;
    amount: string;
    limitPrice: string;
};

const LimitPreview: React.FC<LimitPreviewProps> = ({
    userPosition,
    amount,
    limitPrice,
}) => {
    const tokenStats = useTokenStats();
    const { userFee } = useUserFee();

    const pnlValues = userPosition
        ? calculatePnl(
              userPosition,
              tokenStats?.[userPosition.tradePair.chartSymbol]?.price
          )
        : undefined;

    const removedPercentage = useMemo(
        () =>
            parseEther(amount || '0')
                .mul(parseEther('1'))
                .div(parseEther(userPosition?.volume || '1')),
        [amount, userPosition?.volume]
    );

    const previewSize = useMemo(
        () =>
            parseEther(userPosition?.volume || '1')
                .mul(parseEther('1').sub(removedPercentage))
                .div(parseEther('1')),
        [userPosition?.volume, removedPercentage]
    );

    const previewCollateral = useMemo(
        () =>
            parseEther(userPosition?.margin || '1')
                .mul(parseEther('1').sub(removedPercentage))
                .div(parseEther('1')),
        [userPosition?.margin, removedPercentage]
    );

    const tpslPnL = useMemo(
        () =>
            limitPrice
                ? parseUnits(limitPrice || '0', config.priceFeedDecimal)
                      .sub(
                          parseUnits(
                              userPosition?.entryPrice || '0',
                              config.priceFeedDecimal
                          )
                      )
                      .mul(userPosition?.isShort ? -1 : 1)
                      .mul(parseUnits('1', config.priceFeedDecimal))
                      .div(
                          parseUnits(
                              userPosition?.entryPrice || '1',
                              config.priceFeedDecimal
                          )
                      )
                      .mul(
                          parseUnits(userPosition?.volume || '1', config.priceFeedDecimal)
                      )
                      .div(parseUnits('1', config.priceFeedDecimal))
                      .sub(
                          parseUnits(
                              userPosition?.totalFeeAmount || '0',
                              config.priceFeedDecimal
                          )
                      )
                : parseUnits(pnlValues?.pnl || '0', config.priceFeedDecimal),
        [
            limitPrice,
            userPosition?.entryPrice,
            userPosition?.isShort,
            userPosition?.volume,
            userPosition?.totalFeeAmount,
            pnlValues?.pnl,
        ]
    );

    const closeFee = useMemo(
        () =>
            parseEther(userPosition?.volume || '0')
                .sub(previewSize)
                .mul(userFee || 0)
                .div(100_00),
        [userPosition?.volume, previewSize, userFee]
    );

    const previewPnL = useMemo(
        () =>
            tpslPnL
                .mul(
                    parseUnits('1', config.priceFeedDecimal).sub(
                        parseUnits(
                            removedPercentage.toString(),
                            config.priceFeedDecimal - 18
                        )
                    )
                )
                .div(parseUnits('1', config.priceFeedDecimal)),
        [tpslPnL, removedPercentage]
    );

    return (
        <ClosePositionPreview
            positionDetails={{
                tradePair: userPosition?.tradePair,
                bankruptcyPrice: {
                    value: userPosition?.bankruptcyPrice || '0',
                    previewValue: removedPercentage.gt(0)
                        ? userPosition?.bankruptcyPrice || '0'
                        : undefined,
                },
                collateral: {
                    value: userPosition?.margin || '0',
                    previewValue: removedPercentage.gt(0)
                        ? formatNonNegative(previewCollateral)
                        : undefined,
                },
                entryPrice: {
                    value: userPosition?.entryPrice || '0',
                    previewValue: removedPercentage.gt(0)
                        ? userPosition?.entryPrice || '0'
                        : undefined,
                },
                fees: {
                    value: undefined,
                    previewValue: closeFee.gt(0)
                        ? formatEther(closeFee || '0')
                        : undefined,
                },
                isShort: userPosition?.isShort ?? false, // should never happen, but just in case
                leverage: {
                    value: userPosition?.leverage || '0',
                    previewValue: removedPercentage.gt(0)
                        ? userPosition?.leverage || '0'
                        : undefined,
                },
                markPrice: {
                    value: '0',
                },
                pnl: {
                    value: formatUnits(tpslPnL || '0', config.priceFeedDecimal),
                    previewValue: removedPercentage.gt(0)
                        ? formatUnits(previewPnL, config.priceFeedDecimal)
                        : undefined,
                },
                size: {
                    value: userPosition?.volume || '0',
                    previewValue: removedPercentage.gt(0)
                        ? formatNonNegative(previewSize)
                        : undefined,
                },
            }}
        />
    );
};

export default LimitPreview;
